// Icon lookup for the homepage tool grid.
//
// A tool's icon used to be a component reference held directly on the entry in
// tools.ts. Once the lineup lives in a database that stops being possible — a
// row can hold a string, not a React component — so the string is resolved
// here.
//
// Follows the pattern already set by `src/barnApps/tileIcons.ts`, and for the
// reason stated there: a lookup table means a missing key is one obvious gap in
// this file, rather than a silently generic icon out in the grid.
//
// ── Two rules that keep this safe for the prerender ───────────────────────
//
// 1. This module imports ONLY from `lucide-react`. Nothing here may touch
//    `window`, `document` or `localStorage`, because the homepage is rendered
//    to a string under Node at build time (`scripts/prerender-rbhome.mjs`) and
//    anything browser-only throws there at module scope.
//
// 2. `resolveToolIcon` NEVER throws and NEVER returns undefined. An undefined
//    component placed into JSX throws inside `renderToString`, which fails the
//    whole build via the prerender script's minimum-bytes guard. An icon name
//    typed wrong by an admin must degrade to a neutral glyph and let the row
//    keep working — the destination still resolves, the label still reads.
//
// Keys are the lucide export name VERBATIM ("Map", not "map" or "map-icon").
// No transformation step means no transformation bug, and the admin picker
// shows these same strings.

import {
  Beef,
  ChartColumn,
  Circle,
  ClipboardList,
  CloudSun,
  DollarSign,
  Factory,
  Fuel,
  Globe,
  LayoutList,
  Leaf,
  Map,
  MapPin,
  Newspaper,
  PlayCircle,
  Sparkles,
  Sprout,
  TrendingUp,
  Trophy,
  Warehouse,
  Zap,
  type LucideIcon,
} from "lucide-react";

/**
 * Every icon a tool may use. Add here first, then it becomes selectable in the
 * admin picker — the picker's options are this object's keys, which is what
 * makes an unrenderable name impossible to author through the UI.
 */
export const RB_ICONS: Record<string, LucideIcon> = {
  Beef,
  ChartColumn,
  ClipboardList,
  CloudSun,
  DollarSign,
  Factory,
  Fuel,
  Globe,
  LayoutList,
  Leaf,
  Map,
  MapPin,
  Newspaper,
  PlayCircle,
  Sparkles,
  Sprout,
  TrendingUp,
  Trophy,
  Warehouse,
  Zap,
};

/** The names an admin may choose from, sorted for a stable picker. */
export const RB_ICON_NAMES: string[] = Object.keys(RB_ICONS).sort();

/**
 * Resolve a stored icon name to a component.
 *
 * Total by construction — see rule 2 above. An unknown name yields `Circle`,
 * which reads as "an icon we don't have" rather than as a broken page.
 */
export function resolveToolIcon(name: string | null | undefined): LucideIcon {
  if (!name) return Circle;
  return RB_ICONS[name] ?? Circle;
}
